import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import { CAMERA_TYPES, DEVICE_TYPES } from '@/Utils/vice-constants';
import { generateCameraData } from '@/Utils/helpers';

function useExternalCameraComposable() {
  const multiCameraDevicesStore = useLocalStreamingDevicesStore();
  const isDeviceSetupChanged = multiCameraDevicesStore.isSessionSetupWasChanged;

  // Set main camera
  const setMainCamera = async () => {
    const cameraName = CAMERA_TYPES.INTERNAL_WEBCAM;
    const predefinedCamLabel = getStoredDeviceProperty(DEVICE_TYPES.MAIN_CAMERA, 'label');
    const predefinedCamConnectedId = idConnectedByLabel(predefinedCamLabel);

    if (!isDeviceSetupChanged && predefinedCamConnectedId) {
      const predefinedCamId = getStoredDeviceProperty(DEVICE_TYPES.MAIN_CAMERA);
      multiCameraDevicesStore.setMainCameraDeviceId(predefinedCamId);
    }

    // Is INTERNAL camera in cache and available
    const cachedMainCamId = multiCameraDevicesStore.cameraTypes[cameraName].cameraDeviceId;
    const cachedMainCamAvailable = isConnectedById(cachedMainCamId);

    if (cachedMainCamId && cachedMainCamAvailable) {
      multiCameraDevicesStore.setMainCameraDeviceId(cachedMainCamId);
    }
  };

  // check if EXTERNAL camera should be added
  const handleExternalCameraConnection = async () => {
    const cameraName = CAMERA_TYPES.EXTERNAL_WEBCAM;

    const predefinedExCamLabel = getStoredDeviceProperty(DEVICE_TYPES.EXTRA_CAMERA, 'label');
    const predefinedExCamConnected = idConnectedByLabel(predefinedExCamLabel);

    if (!isDeviceSetupChanged && predefinedExCamConnected) {
      await multiCameraDevicesStore.addExternalCameraToCache(
        predefinedExCamConnected.deviceId,
        predefinedExCamLabel,
      );
    }

    // Check if external camera in cache and available
    const cachedExCam = multiCameraDevicesStore.connectedCameras.find(
      (camera) => camera.name === CAMERA_TYPES.EXTERNAL_WEBCAM,
    );

    const cachedExCamAvailable = isConnectedById(cachedExCam?.cameraDeviceId);
    if (cachedExCam && cachedExCamAvailable) {
      await addExternalCamera(cameraName, cachedExCam.cameraDeviceId);
    }
  };

  // Set extra camera
  const addExternalCamera = async (cameraName, cameraId) => {
    const isAddedOnInit = true;
    multiCameraDevicesStore.setCurrentActiveCameraType(cameraName);

    const camData = generateCameraData(
      cameraName,
      multiCameraDevicesStore.allCameraTypes[cameraName]?.cameraDeviceId || cameraId,
      isAddedOnInit,
    );

    await multiCameraDevicesStore.addCameraType(cameraName, {
      [cameraName]: camData,
    });
  };

  const isConnectedById = (id) =>
    !!multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.VIDEO.Camera.find(
      (device) => device.deviceId === id,
    );

  const idConnectedByLabel = (label) =>
    multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.VIDEO.Camera.find(
      (device) => device.label === label,
    );

  // Get stored device ID by device label
  const getStoredDeviceProperty = (deviceLabel, property = 'deviceId') => {
    const storedDevice = multiCameraDevicesStore.clinicianDevicesForSession.clinician_devices.find(
      (device) => device.device_type_label === deviceLabel,
    );

    return storedDevice ? storedDevice.device_data[property] : null;
  };

  return {
    setMainCamera,
    isConnectedById,
    addExternalCamera,
    idConnectedByLabel,
    handleExternalCameraConnection,
  };
}

export default useExternalCameraComposable;
